import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { Timesheet } from './models/Timesheet';

const TimesheetContext = createContext();

export const TimesheetProvider = ({ children }) => {
    const [timesheets, setTimesheets] = useState(() => {
        const savedData = localStorage.getItem('timesheets');
        if (!savedData) return {};
        
        try {
            const parsed = JSON.parse(savedData);
            return Object.entries(parsed).reduce((acc, [key, value]) => {
                acc[key] = Timesheet.fromJSON(value);
                return acc;
            }, {});
        } catch (e) {
            console.error('Error parsing saved timesheets:', e);
            return {};
        }
    });

    useEffect(() => {
        const serialized = Object.entries(timesheets).reduce((acc, [key, value]) => {
            acc[key] = value.toJSON();
            return acc;
        }, {});
        localStorage.setItem('timesheets', JSON.stringify(serialized));
    }, [timesheets]);

    const updateTimesheet = useCallback((filename, data) => {
        setTimesheets(prev => ({
            ...prev,
            [filename]: new Timesheet({
                ...prev[filename]?.toJSON(),
                ...data
            })
        }));
    }, []);

    const toggleChecked = useCallback((filename) => {
        setTimesheets(prev => ({
            ...prev,
            [filename]: new Timesheet({
                ...prev[filename]?.toJSON(),
                isChecked: !prev[filename]?.isChecked
            })
        }));
    }, []);

    const removeTimesheet = useCallback((filename) => {
        setTimesheets(prev => {
            const { [filename]: removed, ...remaining } = prev;
            return remaining;
        });
    }, []);

    const value = {
        timesheets,
        updateTimesheet,
        toggleChecked,
        removeTimesheet
    };

    return (
        <TimesheetContext.Provider value={value}>
            {children}
        </TimesheetContext.Provider>
    );
};

export const useTimesheetData = () => {
    const context = useContext(TimesheetContext);
    if (!context) {
        throw new Error('useTimesheetData must be used within a TimesheetProvider');
    }
    return context;
};
